import { useState } from 'react';
import { KeyRound, Plus, Power } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { Can } from '../../../shared/components/Can';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { TextInput } from '../../../shared/components/TextInput';
import { useToast } from '../../../shared/components/Toast';
import { FEATURES } from '../../../shared/hooks/usePermissions';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import { maskCPF, maskPhone, validateCPF } from '../../../shared/utils/validators';
import { PessoasModal } from '../../pessoas/components/PessoasModal';
import { AcessoExterno, ResultadoAcesso } from '../api/acessosExternos';
import { useContatos } from '../hooks';
import {
  useAcessosExternos, useCriarAcessoExterno, useReenviarAcessoExterno, useSituacaoAcessoExterno,
} from '../hooks/acessosExternos';

/**
 * Acessos externos do cliente (change `acesso-gestor-cliente`): usuários do
 * cliente que entram no portal, sempre restritos a este cliente.
 */
export function ClienteAcessosExternos({ clientId }: { clientId: string }) {
  const toast = useToast();
  const acessos = useAcessosExternos(clientId);
  const situacao = useSituacaoAcessoExterno(clientId);
  const reenviar = useReenviarAcessoExterno(clientId);
  const [novo, setNovo] = useState(false);
  const [confirmar, setConfirmar] = useState<{ kind: 'situacao' | 'reenviar'; item: AcessoExterno } | null>(null);
  const [link, setLink] = useState<string | null>(null);

  function avisar(r: ResultadoAcesso, ok: string) {
    if (r.welcomeEmailSent === false && r.passwordSetupLink) {
      setLink(r.passwordSetupLink.url);
      toast.error('O e-mail não foi enviado: repasse o link de acesso.');
    } else {
      toast.success(ok);
    }
  }

  async function confirmarAcao() {
    if (!confirmar) return;
    const { kind, item } = confirmar;
    try {
      if (kind === 'situacao') {
        await situacao.mutateAsync({ userId: item.id, active: !item.active });
        toast.success(item.active ? 'Acesso inativado: as sessões foram encerradas.' : 'Acesso reativado.');
      } else {
        avisar(await reenviar.mutateAsync(item.id), 'Link de acesso enviado por e-mail.');
      }
    } catch (err) {
      toast.error(getFriendlyErrorMessage(err, 'Não foi possível concluir a ação.'));
    } finally {
      setConfirmar(null);
    }
  }

  const lista = acessos.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Acessos externos</h2>
          <p className="text-xs text-gray-500">Usuários do cliente no portal. Eles só veem dados deste cliente.</p>
        </div>
        <Can feature={FEATURES.clientesAcessosExternos} action="create">
          <Button size="sm" onClick={() => setNovo(true)}><Plus className="mr-1 h-4 w-4" /> Novo acesso</Button>
        </Can>
      </div>

      {link && (
        <p className="break-all rounded bg-amber-50 p-2 text-xs text-amber-900">
          Link de acesso (repasse ao usuário): {link}
        </p>
      )}

      {acessos.isLoading ? (
        <p className="text-sm text-gray-500">Carregando…</p>
      ) : lista.length === 0 ? (
        <p className="text-sm text-gray-500">Nenhum acesso externo.</p>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
          {lista.map((a) => (
            <li key={a.id} className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm ${a.active ? '' : 'opacity-60'}`}>
              <div>
                <p className="font-medium text-gray-900">
                  {a.name}
                  {!a.active && <span className="ml-2 text-xs font-normal text-gray-500">inativo</span>}
                  {a.active && a.pendingFirstAccess && <span className="ml-2 text-xs font-normal text-amber-700">primeiro acesso pendente</span>}
                </p>
                <p className="text-gray-600">
                  {a.email}{a.phone ? ` · ${a.phone}` : ''}
                  {a.lastSessionAt && <span className="text-xs text-gray-500"> · último acesso {new Date(a.lastSessionAt).toLocaleString('pt-BR')}</span>}
                </p>
              </div>
              <Can feature={FEATURES.clientesAcessosExternos} action="edit">
                <span className="flex gap-1">
                  {a.active && (
                    <button type="button" className="rounded p-1.5 text-gray-500 hover:bg-gray-100" title="Reenviar acesso"
                      aria-label={`Reenviar acesso de ${a.name}`} onClick={() => setConfirmar({ kind: 'reenviar', item: a })}>
                      <KeyRound className="h-4 w-4" />
                    </button>
                  )}
                  <button type="button" className="rounded p-1.5 text-gray-500 hover:bg-gray-100" title={a.active ? 'Inativar' : 'Reativar'}
                    aria-label={a.active ? `Inativar ${a.name}` : `Reativar ${a.name}`} onClick={() => setConfirmar({ kind: 'situacao', item: a })}>
                    <Power className="h-4 w-4" />
                  </button>
                </span>
              </Can>
            </li>
          ))}
        </ul>
      )}

      <NovoAcessoModal clientId={clientId} open={novo} onClose={() => setNovo(false)} onCreated={(r) => { setNovo(false); avisar(r, 'Acesso criado e enviado por e-mail.'); }} />
      <ConfirmDialog
        open={confirmar !== null}
        title={confirmar?.kind === 'reenviar' ? 'Reenviar acesso?' : confirmar?.item.active ? 'Inativar acesso?' : 'Reativar acesso?'}
        message={confirmar?.kind === 'reenviar'
          ? 'O usuário recebe por e-mail um link para definir nova senha. A senha atual continua valendo até ele usar o link.'
          : confirmar?.item.active ? 'O usuário perde o acesso imediatamente.' : 'O usuário volta a entrar com a senha atual.'}
        danger={confirmar?.kind === 'situacao' && !!confirmar?.item.active}
        busy={situacao.isPending || reenviar.isPending}
        onConfirm={confirmarAcao}
        onCancel={() => setConfirmar(null)}
      />
    </div>
  );
}

function NovoAcessoModal({ clientId, open, onClose, onCreated }: {
  clientId: string; open: boolean; onClose: () => void; onCreated: (r: ResultadoAcesso) => void;
}) {
  const criar = useCriarAcessoExterno(clientId);
  const contatos = useContatos(open ? clientId : undefined);
  const [contactId, setContactId] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [cpf, setCpf] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);

  const ativos = (contatos.data?.data ?? []).filter((c) => c.status === 'ativo');

  function escolherContato(id: string) {
    setContactId(id);
    const c = ativos.find((x) => x.id === id);
    if (c) {
      setName(c.nome);
      setEmail(c.email);
      setPhone(c.celular ?? c.telefone ?? '');
    }
  }

  function fechar() {
    setContactId(''); setName(''); setEmail(''); setCpf(''); setPhone(''); setError(null);
    onClose();
  }

  async function submit() {
    if (!contactId && (!name.trim() || !email.trim())) return setError('Escolha um contato ou informe nome e e-mail.');
    if (!validateCPF(cpf)) return setError('CPF inválido.');
    try {
      const r = await criar.mutateAsync({
        contactId: contactId || undefined, name: name.trim() || undefined, email: email.trim() || undefined,
        cpf: cpf.replace(/\D/g, ''), phone: phone.trim() || undefined,
      });
      setContactId(''); setName(''); setEmail(''); setCpf(''); setPhone(''); setError(null);
      onCreated(r);
    } catch (err) {
      setError(getFriendlyErrorMessage(err, 'Não foi possível criar o acesso.'));
    }
  }

  return (
    <PessoasModal open={open} title="Novo acesso externo"
      description="O usuário recebe por e-mail uma senha temporária e entra no portal do cliente."
      submitLabel="Criar acesso" isSubmitting={criar.isPending} submitError={error} onClose={fechar} onSubmit={submit}>
      <AutocompleteSelect label="Contato do cliente (opcional)"
        options={[{ value: '', label: 'Sem contato' }, ...ativos.map((c) => ({ value: c.id, label: `${c.nome} — ${c.email}` }))]}
        value={contactId} onChange={escolherContato} />
      <TextInput label="Nome" value={name} onChange={(e) => setName(e.target.value)} />
      <TextInput label="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <TextInput label="CPF" value={cpf} onChange={(e) => setCpf(maskCPF(e.target.value))} />
        <TextInput label="Telefone" value={phone} onChange={(e) => setPhone(maskPhone(e.target.value))} />
      </div>
    </PessoasModal>
  );
}
